import { Link, createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Building2, MapPin } from 'lucide-react'
import { DashboardLayout } from '@/components/layout'
import { Card, CardContent } from '@/components/ui/card'
import { listClients } from '@/lib/api'
import { atsNav } from '@/lib/atsNav'
import { PageHeader } from '@/lib/atsUi'
import { currentTenant, currentUser } from '@/lib/mocks'

export const Route = createFileRoute('/clients/')({ component: ClientsPage })

const nf = (n: number) => n.toLocaleString()

function ClientsPage() {
  const { data, isLoading } = useQuery({ queryKey: ['clients'], queryFn: listClients })
  const clients = data?.clients ?? []
  const openRoles = clients.reduce((a, c) => a + c.openRoles, 0)
  const totalRoles = clients.reduce((a, c) => a + c.totalRoles, 0)
  const shortlisted = clients.reduce((a, c) => a + c.shortlisted, 0)

  return (
    <DashboardLayout title="Clients" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <PageHeader title="Clients" subtitle="The companies you hire for — extracted from your jobs. Click one for its dashboard." />

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Metric label="Clients" value={isLoading ? '·' : nf(clients.length)} />
        <Metric label="Roles" value={isLoading ? '·' : nf(totalRoles)} />
        <Metric label="Open roles" value={isLoading ? '·' : nf(openRoles)} />
        <Metric label="Candidates shortlisted" value={isLoading ? '·' : nf(shortlisted)} />
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5 font-medium">Client</th>
                  <th className="px-4 py-2.5 font-medium">Location</th>
                  <th className="px-4 py-2.5 font-medium">Open</th>
                  <th className="px-4 py-2.5 font-medium">Roles</th>
                  <th className="px-4 py-2.5 font-medium">Screened</th>
                  <th className="px-4 py-2.5 font-medium">Shortlisted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {clients.length === 0 && !isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">
                      No clients yet — create a job with a company (the JD's company is auto-extracted) on the <Link to="/jobs" className="text-primary underline">Jobs</Link> page.
                    </td>
                  </tr>
                ) : (
                  clients.map((c) => (
                    <tr key={c.name} className="group transition-colors hover:bg-muted/40">
                      <td className="px-4 py-2.5">
                        <Link to="/clients/$clientId" params={{ clientId: c.name }} className="flex items-center gap-2.5">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"><Building2 className="h-4 w-4" /></span>
                          <span className="font-medium text-foreground group-hover:underline">{c.name}</span>
                        </Link>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-muted-foreground">{c.location ? <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{c.location}</span> : '—'}</td>
                      <td className="px-4 py-2.5 text-muted-foreground tabular-nums">{c.openRoles}</td>
                      <td className="px-4 py-2.5 text-muted-foreground tabular-nums">{c.totalRoles}</td>
                      <td className="px-4 py-2.5 text-muted-foreground tabular-nums">{c.screenedRoles}</td>
                      <td className="px-4 py-2.5 text-muted-foreground tabular-nums">{nf(c.shortlisted)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </DashboardLayout>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-muted/40 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-2xl font-medium">{value}</div>
    </div>
  )
}
